import React from 'react'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import historyStore from '../../../../store/historyStore.ts'
import { LatLng } from '../../types.ts'
import mapStore from '../../../../store/mapStore.ts'
import { createZoomControl } from './zoomControl.ts'
import controlStore from '../../../../store/controlStore.ts'

const libraries = ['maps', 'geometry', 'places', 'drawing', 'marker', 'routes', 'geocoding']
export const POI_OFF = {
		featureType: "poi",
		stylers: [{ visibility: "off" }],
	}

interface Props {
	height: string
	center: LatLng
	zoom: number
	children?: React.ReactNode
	isHistory?: boolean
}
export function GoogleMapWrapper(props: Props) {
	const {height, center, zoom} = props
	const isMobile = controlStore(s => s.isOnMobileDevice)

	const mapOptions = {
		// center and zoom are set programmatically in onLoad (from saved state or props).
		// Do NOT include them here — the GoogleMap component reapplies options on every
		// render, which would snap the map back to these values and block user zoom/pan.
		fullscreenControl: false,
		styles: [POI_OFF],
		disableDefaultUI: true,
		keyboardShortcuts: !isMobile,
	}

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
		// @ts-ignore => no good cast for Libraries
		libraries,
	})

	const onLoad = React.useCallback(function callback(map: google.maps.Map) {
		const savedState = JSON.parse(localStorage.getItem('mapState') || '{}')
		if (savedState.center && savedState.zoom) {
			map.setCenter(savedState.center)
			map.setZoom(savedState.zoom)
			mapStore.getState().setZoom(savedState.zoom)
		} else {
			map.setCenter(center)
			map.setZoom(zoom)
			mapStore.getState().setZoom(zoom)
		}
		
		map.addListener('click',  () => {
			mapStore.getState().setMarkerInfo(null)
		})
		
		map.addListener('center_changed', () => {
			if (window.location.pathname.includes('history')) return;
			const newCenter = map.getCenter()
			const currentZoom = map.getZoom()
			if (newCenter && currentZoom) {
				const timeoutId = setTimeout(() => {
					localStorage.setItem('mapState', JSON.stringify({
						zoom: currentZoom,
						center: { lat: newCenter.lat(), lng: newCenter.lng() }
					}))
				}, 500)
				;(map as any)._saveTimeout = timeoutId
			}
		})

		mapStore.getState().setMap(map)
		historyStore.getState().setMap(map)

		if (!isMobile) {
			const controls: any = map.controls[google.maps.ControlPosition.RIGHT_BOTTOM]
			if (controls && !controls.length) {
				createZoomControl(map)
			}
		}

		map.addListener('zoom_changed', () => {
			const currentZoom = map.getZoom()
			if (currentZoom != null) {
				if (!window.location.pathname.includes('history')) {
					mapStore.getState().setZoom(currentZoom)
				} else {
					mapStore.getState().setHistoryZoom(currentZoom)
				}
			}
		})
	}, [])

	const onUnmount = React.useCallback(function callback() {
		mapStore.getState().setMap(null)
		historyStore.getState().setMap(null)
	}, [])

	const containerStyle = {
		width: '100%',
		height,
	}

	return isLoaded ? (
		<GoogleMap
			mapContainerStyle={containerStyle}
			onLoad={onLoad}
			onUnmount={onUnmount}
			options={mapOptions}
		>
			{props.children}
		</GoogleMap>
	) : <></>
}

export default React.memo(GoogleMapWrapper)
